import { BittsButtonProps } from '@crossbeam/bitts';
import { EVENT_SITES, TEAM_CTAS } from '@crossbeam/itly';

import { storeToRefs } from 'pinia';

import useBilling from '@/composables/useBilling';
import { useConnectorTrialEndDate } from '@/composables/useConnectorTrialEndDate';
import useSeats from '@/composables/useSeats';
import { useBillingStore } from '@/stores';

export default function useTeamUpsellCTAs() {
  const { blockSalesSeatsPPV5 } = useSeats();
  const {
    isSupernodeOrEnterpriseTier,
    hasSubscription,
    hasPaymentIssue,
    isNotPaying,
  } = storeToRefs(useBillingStore());

  const { handleBillingInteraction } = useBilling();
  const { isTrueConnector } = useConnectorTrialEndDate();

  function getCoreTalkToSalesTooltipText(isSales: boolean) {
    if (isSupernodeOrEnterpriseTier.value) {
      const seatType = isSales ? 'Sales' : 'Full Access';
      return `You are using all available ${seatType} seats in your current contract. Talk to Sales to add more seats`;
    }
    if (isSales) {
      return 'Upgrade to Supernode unlock access to Sales seats';
    }
    return 'Unblock your team by adding more seats to Crossbeam';
  }

  function getIntegrationTalkToSalesTooltipText() {
    return 'You are using all available Integration seats in your current contract. Talk to Sales to add more seats';
  }
  const billingInteractionBase = {
    cta: TEAM_CTAS.MORE_SEATS,
    event_site: EVENT_SITES.TEAM_COUNTER,
  };

  function openAddSeatsModal(emit: (event: string) => void) {
    handleBillingInteraction(billingInteractionBase, null, true, null, true);
    emit('open-add-seats-modal');
  }

  function getIntegrationButtonInfo(
    isAtLimit: boolean,
  ): BittsButtonProps & { tooltipText?: string; click?: () => void } {
    if (!isAtLimit) return {};
    return {
      type: 'upsell',
      text: 'Talk to Sales',
      leftIcon: ['fak', 'upgrade'],
      tooltipText: getIntegrationTalkToSalesTooltipText(),
      click: () => {
        handleBillingInteraction({
          ...billingInteractionBase,
          talkToSalesReason: 'More Integration Seats',
        });
      },
    };
  }

  function getButtonInfo(
    isSales: boolean,
    isAtLimit: boolean,
    emit: (event: string) => void,
  ): BittsButtonProps & {
    tooltipText?: string;
    click?: () => void;
  } {
    if (
      (isSales &&
        (isNotPaying.value || isTrueConnector.value) &&
        blockSalesSeatsPPV5.value) ||
      (isTrueConnector.value && !hasSubscription.value && isAtLimit) ||
      (isSupernodeOrEnterpriseTier.value && isAtLimit)
    ) {
      return {
        type: 'upsell',
        text: 'Talk to Sales',
        leftIcon: ['fak', 'upgrade'],
        tooltipText: getCoreTalkToSalesTooltipText(isSales),
        click: () => {
          handleBillingInteraction({
            ...billingInteractionBase,
            talkToSalesReason: `More ${isSales ? 'Sales' : 'Full Access'} Seats`,
          });
        },
      };
    } else if (isNotPaying.value && isAtLimit) {
      return {
        type: 'upsell',
        text: 'Upgrade',
        leftIcon: ['fak', 'upgrade'],
        tooltipText: 'Unblock your team by adding more seats to Crossbeam',
        click: () => {
          handleBillingInteraction(billingInteractionBase);
        },
      };
    } else if (isTrueConnector.value && hasSubscription.value) {
      const addSeatsDisabled = hasSubscription.value && hasPaymentIssue.value;
      return {
        variant: 'outline',
        leftIcon: ['fak', 'add'],
        text: 'Add seats',
        disabled: addSeatsDisabled,
        tooltipText: addSeatsDisabled
          ? 'Update your payment method to add seats'
          : undefined,
        click: addSeatsDisabled
          ? undefined
          : () => {
              blockSalesSeatsPPV5.value
                ? openAddSeatsModal(emit)
                : handleBillingInteraction(
                    billingInteractionBase,
                    {
                      cancelDestination: '/billing',
                      core: isSales ? 0 : 1,
                      sales: isSales ? 1 : 0,
                    },
                    true,
                  );
            },
      };
    }
    return {};
  }
  return { getButtonInfo, getIntegrationButtonInfo };
}
